// The two COAC lifecycles, in one place.
//
// ADR-0022 keeps `electionStatus` and `opsStatus` orthogonal. `electionStatus`
// says how far the ELECTION has travelled (Ops routes it, the IM decides it).
// `opsStatus` says whether OPS has finalised the whole corporate action and sent
// it to the custodian. A corporate action can sit in any combination of the two.
//
// Before this module every surface collapsed the two axes by hand, so the copy
// drifted and the legacy-row rule was spelled at four call sites. This module is
// the ONE owner of both phase readers on the web side. Do not write the
// derivation again at a page, in the same way that `filters.ts` owns the
// mandatory / voluntary predicate.
//
// The module is pure: no React, no clock and no network. Every reader takes the
// smallest row shape that answers the question, so a narrow projection (for
// example `CoacResponsesRow`, which carries `opsStatus` only) can use it.
//
// WORDS AND COLOURS STAY IN THE VIEW. This module gives the phase and the
// exhaustive lookup; each surface declares its own `CoacElectionPresentation` /
// `CoacOpsPresentation` record and owns every label and every tone. A rename is
// a product decision, so the module never imposes one label set on two surfaces.

import type { CoacElectionStatus, CoacOpsStatus } from '@septima/contract';
import type { PillVariant } from '@/components/ui/Pill';

// ---------------------------------------------------------------------------
// Row shapes: the minimum each reader needs.
// ---------------------------------------------------------------------------

/** Any row that carries the election axis. */
export interface CoacElectionRow {
    readonly electionStatus: CoacElectionStatus;
}

/** Any row that carries the Ops-completion axis. */
export interface CoacOpsRow {
    readonly opsStatus: CoacOpsStatus | null;
}

/** A row that carries both axes, so the combined questions can be asked. */
export type CoacLifecycleRow = CoacElectionRow & CoacOpsRow;

// ---------------------------------------------------------------------------
// The two phases.
// ---------------------------------------------------------------------------

/**
 * How far the election has travelled.
 *   `awaiting_routing`: Ops has not routed it yet.
 *   `with_im`:          an IM must decide.
 *   `decided`:          the election is settled, by approval or by default.
 */
export type CoacElectionPhase = 'awaiting_routing' | 'with_im' | 'decided';

/**
 * Whether Ops has finalised the corporate action.
 *   `awaiting`:  Ops has not completed it.
 *   `completed`: Ops has sent the elections, and the Desk is read-only.
 */
export type CoacOpsPhase = 'awaiting' | 'completed';

/** Read the election axis. Exhaustive, so a new `electionStatus` stops the build. */
export function electionPhase(row: CoacElectionRow): CoacElectionPhase {
    switch (row.electionStatus) {
        case 'Pending':
            return 'awaiting_routing';
        case 'RoutedToIm':
            return 'with_im';
        case 'ElectionApproved':
        case 'ElectionDefaulted':
            return 'decided';
    }
}

/**
 * Read the Ops-completion axis.
 *
 * THE LEGACY-ROW RULE LIVES HERE, ONCE. `opsStatus` is nullable: a row written
 * before the Ops-completion beat existed carries null. A null row has not been
 * completed, so it reads as `awaiting`. `CompleteCoacAction` declares the same
 * rule for the write side, and it is the only route to `Completed`.
 */
export function opsPhase(row: CoacOpsRow): CoacOpsPhase {
    return row.opsStatus === 'Completed' ? 'completed' : 'awaiting';
}

// ---------------------------------------------------------------------------
// The combined questions.
// ---------------------------------------------------------------------------

/**
 * Is this corporate action still someone's job?
 *
 * It is open while the election is not settled AND Ops has not finalised it.
 * Ops completion closes the action for everybody: the Desk cannot record a
 * further account election, so an undecided election on a completed action is
 * nobody's work.
 */
export function isOpen(row: CoacLifecycleRow): boolean {
    return electionPhase(row) !== 'decided' && opsPhase(row) === 'awaiting';
}

/**
 * Is the Desk read-only for this corporate action?
 *
 * Ops completion is the one gate. The IM keeps the card and the recorded
 * elections, but every control is disabled. The `record-coac-account-election`
 * precondition refuses a write on the same rule, so the view and the Action
 * agree.
 */
export function isDeskReadOnly(row: CoacOpsRow): boolean {
    return opsPhase(row) === 'completed';
}

// ---------------------------------------------------------------------------
// Pills. The module gives the phase, and the surface gives the words.
// ---------------------------------------------------------------------------

/** What a surface renders for one phase. */
export interface CoacPill {
    label: string;
    variant: PillVariant;
}

/**
 * A surface's election pill copy. The record is exhaustive over
 * `electionStatus`, not over `CoacElectionPhase`: the two settled values keep
 * their own words and tones today ("Election approved" in green, "Defaulted" in
 * grey), and that split is copy, not business rule.
 */
export type CoacElectionPresentation = Readonly<Record<CoacElectionStatus, CoacPill>>;

/** A surface's Ops-completion pill copy, keyed by phase so the null rule applies. */
export type CoacOpsPresentation = Readonly<Record<CoacOpsPhase, CoacPill>>;

/** The election pill for a row, in the calling surface's own words. */
export function electionPill(
    row: CoacElectionRow,
    presentation: CoacElectionPresentation,
): CoacPill {
    return presentation[row.electionStatus];
}

/** The Ops-completion pill for a row, in the calling surface's own words. */
export function opsPill(row: CoacOpsRow, presentation: CoacOpsPresentation): CoacPill {
    return presentation[opsPhase(row)];
}
